import { Brand } from '@evenfinancial/even-ts-static'
import * as React from 'react'
import { noop } from 'ui/multi-product/constants'
import { lazyInitTrustpilotWidget } from './trustpilotHelperScript'
import useIsElementInView from './useElementInView'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Trustpilot?: any
  }
}

export type TrustpilotWidgetType =
  | 'list'
  | 'slider'
  | 'microStar'
  | 'carousel'
  | 'mini'
  | 'miniCarousel'
  | 'miniCarouselNoArrows'
type VoidCallback = () => void

interface Props {
  brand: Brand
  widget: TrustpilotWidgetType
  ratingsVisible?: 'all' | 'onlyGood'
  dataTag?: string | ''
}

interface TrustpilotBusinessData {
  businessId: string
  url: string
}

const fionaTrustpilotData: TrustpilotBusinessData = {
  businessId: '5fb2c5248c265c0001186720',
  url: 'https://www.trustpilot.com/review/fiona.com',
}

const leaplifeTrustpilotData: TrustpilotBusinessData = {
  businessId: '5a4681d6b894c903d4a0f880',
  url: 'https://www.trustpilot.com/review/www.leaplife.com',
}

const trustpilotBrandConfig = {
  [Brand.Fiona]: fionaTrustpilotData,
  [Brand.LeapLife]: leaplifeTrustpilotData,
  [Brand.Even]: leaplifeTrustpilotData,
}

interface TrustpilotConfig {
  height: number
  width: string
  templateId: string
}

export const TrustpilotWidgetConfigs: Record<TrustpilotWidgetType, TrustpilotConfig> = {
  list: { height: 600, width: '100%', templateId: '539ad60defb9600b94d7df2c' },
  slider: { height: 250, width: '100%', templateId: '54ad5defc6454f065c28af8b' },
  microStar: { height: 27, width: '270px', templateId: '5419b732fbfb950b10de65e5' },
  carousel: { height: 130, width: '100%', templateId: '53aa8912dec7e10d38f59f36' },
  mini: { height: 130, width: '100%', templateId: '53aa8807dec7e10d38f59f32' },
  miniCarousel: { height: 330, width: '100%', templateId: '539ad0ffdec7e10e686debd7' },
  miniCarouselNoArrows: { height: 250, width: '100%', templateId: '539ad0ffdec7e10e686debd7' },
}

export const TrustpilotWidget = ({ brand, widget, ratingsVisible = 'onlyGood', dataTag }: Props): JSX.Element => {
  const loadFn = React.useRef<VoidCallback>(noop)
  const ref = useIsElementInView(() => {
    loadFn.current()
  })
  React.useEffect(() => {
    loadFn.current = () => {
      lazyInitTrustpilotWidget(ref.current)
      loadFn.current = noop
    }
  })

  const stars = ratingsVisible === 'all' ? '1,2,3,4,5' : '4,5'
  const tag = dataTag || ''
  const widgetConfig = TrustpilotWidgetConfigs[widget]
  const brandConfig = trustpilotBrandConfig[brand]

  return (
    <div
      ref={ref}
      style={{ height: widgetConfig.height, width: widgetConfig.width }}
      className="trustpilot-widget"
      data-locale="en-US"
      data-businessunit-id={brandConfig.businessId}
      data-template-id={widgetConfig.templateId}
      data-style-height={widgetConfig.height}
      data-style-width={widgetConfig.width}
      data-theme="light"
      data-tags={tag}
      data-stars={stars}
      data-review-languages="en"
      data-font-family="Nunito"
      data-text-color="#505050"
    >
      <a href={brandConfig.url} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>
    </div>
  )
}
